/**
 * Shared export utilities for Sales and Returns reports.
 * Generates clean PDF and Excel files from data (not from HTML).
 */

// Number formatter: 1 000.00
function fmt(value) {
  if (value === null || value === undefined || isNaN(value)) return '0.00'
  const num = parseFloat(value)
  const parts = Math.abs(num).toFixed(2).split('.')
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
  return (num < 0 ? '-' : '') + parts.join('.')
}

function fmtDate(d) {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('fr-FR')
}

function fmtTime(d) {
  if (!d) return ''
  return new Date(d).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
}

function getBusiness() {
  try { return JSON.parse(localStorage.business) || {} }
  catch { return {} }
}

function countProducts(saleProducts) {
  if (!saleProducts) return 0
  return saleProducts.reduce(function (t, p) { return t + Math.abs(parseInt(p.quantity)) }, 0)
}

/** Aligné sur SalesReportView : daily_rate par vente ; pas de total/1 en USD pour ventes HTG. */
function saleConversionRate(sale) {
  const dr = parseFloat(sale.daily_rate)
  if (!dr || dr <= 0 || isNaN(dr)) return null
  if (Number(sale.rate_id) === 1 && dr <= 1) return null
  return dr
}

function displayAmountHtg(sale) {
  const t = parseFloat(sale.total)
  if (isNaN(t)) return null
  if (Number(sale.rate_id) === 1) return t
  const dr = saleConversionRate(sale)
  if (dr == null) return null
  return t * dr
}

function displayAmountUsd(sale) {
  const t = parseFloat(sale.total)
  if (isNaN(t)) return null
  if (Number(sale.rate_id) === 2) return t
  const dr = saleConversionRate(sale)
  if (dr == null) return null
  return t / dr
}

/**
 * Build the rows array from sale data, sorted by date ascending (oldest first).
 */
function buildRows(rows, filterRateId) {
  // Sort by created_at ascending (oldest first, most recent at bottom)
  const sorted = [...rows].sort(function (a, b) {
    return new Date(a.created_at) - new Date(b.created_at)
  })

  return sorted.map(function (sale) {
    let amount = ''
    const total = parseFloat(sale.total)

    if (filterRateId == 1) {
      const v = displayAmountHtg(sale)
      amount = v != null ? v : 0
    } else if (filterRateId == 2) {
      const v = displayAmountUsd(sale)
      amount = v != null ? v : 0
    } else {
      amount = total
    }

    return {
      sale_number: sale.sale_number,
      store: sale.store ? sale.store.name : '—',
      agent: sale.user ? sale.user.full_name : '—',
      customer: sale.customer ? sale.customer.full_name : '—',
      amount: Math.abs(amount),
      products: countProducts(sale.sale_products),
      date: fmtDate(sale.created_at),
      time: fmtTime(sale.created_at),
      status: sale.status == 1 ? 'Actif' : 'Annulé',
      currency: filterRateId == 1 ? 'HTG' : (filterRateId == 2 ? 'USD' : (sale.rate_id == 1 ? 'HTG' : 'USD')),
    }
  })
}

// ======================================================
// PDF EXPORT
// ======================================================
export async function exportReportPDF(rows, filterRateId, options) {
  const { jsPDF } = await import('jspdf')
  const title = options.title || 'Rapport'
  const headerColor = options.headerColor || [25, 65, 115]
  const filename = options.filename || 'rapport.pdf'
  const biz = getBusiness()

  const doc = new jsPDF('l', 'mm', 'a4') // landscape
  const pw = 297
  const ph = 210
  const ml = 12
  const mr = pw - 12
  const cw = mr - ml

  const C = {
    primary: headerColor,
    dark: [30, 41, 59],
    muted: [100, 116, 139],
    light: [248, 250, 252],
    border: [220, 225, 230],
    white: [255, 255, 255],
  }

  const dataRows = buildRows(rows, filterRateId)

  // Compute total
  let grandTotal = 0
  let totalProducts = 0
  rows.forEach(function (s) {
    if (s.status == 1) {
      let amt = parseFloat(s.total)
      if (filterRateId == 1) {
        const v = displayAmountHtg(s)
        amt = v != null ? v : 0
      } else if (filterRateId == 2) {
        const v = displayAmountUsd(s)
        amt = v != null ? v : 0
      }
      grandTotal += Math.abs(amt)

      if (s.sale_products) {
        s.sale_products.forEach(function (p) {
          totalProducts += Math.abs(parseFloat(p.quantity))
        })
      }
    }
  })

  const currency = filterRateId == 1 ? 'HTG' : (filterRateId == 2 ? 'USD' : '')

  // ---- HEADER ----
  doc.setFillColor(...C.primary)
  doc.rect(0, 0, pw, 18, 'F')

  doc.setFontSize(12)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(255, 255, 255)
  doc.text(title.toUpperCase(), ml, 11)

  doc.setFontSize(8)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(200, 215, 235)
  const dateRange = localStorage.getItem('debut_periode') && localStorage.getItem('fin_periode')
    ? `${localStorage.getItem('debut_periode')} — ${localStorage.getItem('fin_periode')}`
    : new Date().toLocaleDateString('fr-FR')
  doc.text(dateRange, mr, 8, { align: 'right' })

  if (biz.name) {
    doc.text(biz.name, mr, 13, { align: 'right' })
  }

  let y = 24

  // ---- COLUMN SETUP ----
  const cols = [
    { label: '#', w: 28, align: 'left' },
    { label: 'EMPLACEMENT', w: 38, align: 'left' },
    { label: 'AGENT', w: 38, align: 'left' },
    { label: 'CLIENT', w: 42, align: 'left' },
    { label: 'MONTANT', w: 32, align: 'right' },
    { label: 'PRODUITS', w: 20, align: 'center' },
    { label: 'DATE', w: 24, align: 'center' },
    { label: 'HEURE', w: 18, align: 'center' },
    { label: 'STATUT', w: 20, align: 'center' },
  ]

  // Scale columns to fill width
  const totalW = cols.reduce(function (s, c) { return s + c.w }, 0)
  const scale = cw / totalW
  cols.forEach(function (c) { c.w = Math.round(c.w * scale) })

  // Fix rounding - add remainder to the widest column
  const actualW = cols.reduce(function (s, c) { return s + c.w }, 0)
  cols[3].w += (cw - actualW) // add to CLIENT col

  // Compute x positions
  let cx = ml
  cols.forEach(function (c) { c.x = cx; cx += c.w })

  const rowH = 6.5

  function drawTableHeader() {
    // Header bg
    doc.setFillColor(...C.primary)
    doc.rect(ml, y, cw, rowH + 1, 'F')

    doc.setFontSize(6)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(255, 255, 255)

    cols.forEach(function (c) {
      const tx = c.align === 'right' ? c.x + c.w - 3 : (c.align === 'center' ? c.x + c.w / 2 : c.x + 3)
      doc.text(c.label, tx, y + 4.5, { align: c.align })
    })

    y += rowH + 1
  }

  function drawRow(row, idx) {
    // Alternating bg
    if (idx % 2 === 0) {
      doc.setFillColor(250, 251, 253)
      doc.rect(ml, y, cw, rowH, 'F')
    }

    // Bottom border
    doc.setDrawColor(...C.border)
    doc.setLineWidth(0.1)
    doc.line(ml, y + rowH, mr, y + rowH)

    doc.setFontSize(7)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(...C.dark)

    const vals = [
      row.sale_number,
      row.store.length > 18 ? row.store.substring(0, 16) + '..' : row.store,
      row.agent.length > 18 ? row.agent.substring(0, 16) + '..' : row.agent,
      row.customer.length > 20 ? row.customer.substring(0, 18) + '..' : row.customer,
      fmt(row.amount),
      String(row.products),
      row.date,
      row.time,
      row.status,
    ]

    cols.forEach(function (c, i) {
      const tx = c.align === 'right' ? c.x + c.w - 3 : (c.align === 'center' ? c.x + c.w / 2 : c.x + 3)
      doc.text(vals[i], tx, y + 4.2, { align: c.align })
    })

    y += rowH
  }

  // ---- DRAW TABLE ----
  drawTableHeader()

  dataRows.forEach(function (row, idx) {
    // Page break check (leave room for footer)
    if (y + rowH > ph - 22) {
      doc.addPage()
      y = 12
      drawTableHeader()
    }
    drawRow(row, idx)
  })

  // ---- TOTALS ROW ----
  y += 1
  doc.setFillColor(...C.light)
  doc.rect(ml, y, cw, rowH + 1, 'F')
  doc.setDrawColor(...C.primary)
  doc.setLineWidth(0.4)
  doc.line(ml, y, mr, y)

  doc.setFontSize(7)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(...C.dark)
  doc.text(`TOTAL (${dataRows.length} fiches)`, ml + 3, y + 4.8)
  doc.text(fmt(grandTotal) + (currency ? ' ' + currency : ''), cols[4].x + cols[4].w - 3, y + 4.8, { align: 'right' })
  doc.text(String(totalProducts), cols[5].x + cols[5].w / 2, y + 4.8, { align: 'center' })

  // ---- FOOTER on all pages ----
  const totalPages = doc.internal.getNumberOfPages()
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p)

    doc.setDrawColor(...C.border)
    doc.setLineWidth(0.2)
    doc.line(ml, ph - 10, mr, ph - 10)

    doc.setFontSize(6)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(...C.primary)
    doc.text('KES LAKAY', ml, ph - 6)

    doc.setFont('helvetica', 'normal')
    doc.setTextColor(...C.muted)
    doc.text(`Page ${p} / ${totalPages}`, pw / 2, ph - 6, { align: 'center' })

    const now = new Date()
    doc.text(
      `${now.toLocaleDateString('fr-FR')} ${now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`,
      mr, ph - 6, { align: 'right' }
    )
  }

  doc.save(filename)
}


// ======================================================
// EXCEL EXPORT
// ======================================================
export function exportReportExcel(rows, filterRateId, options) {
  // eslint-disable-next-line no-undef
  const XLSX = require('xlsx')

  const title = options.title || 'Rapport'
  const filename = options.filename || 'rapport.xlsx'
  const biz = getBusiness()

  const dataRows = buildRows(rows, filterRateId)
  const currency = filterRateId == 1 ? 'HTG' : (filterRateId == 2 ? 'USD' : 'HTG/USD')

  // Compute totals
  let grandTotal = 0
  let totalProducts = 0
  rows.forEach(function (s) {
    if (s.status == 1) {
      let amt = parseFloat(s.total)
      if (filterRateId == 1) {
        const v = displayAmountHtg(s)
        amt = v != null ? v : 0
      } else if (filterRateId == 2) {
        const v = displayAmountUsd(s)
        amt = v != null ? v : 0
      }
      grandTotal += Math.abs(amt)

      if (s.sale_products) {
        s.sale_products.forEach(function (p) {
          totalProducts += Math.abs(parseFloat(p.quantity))
        })
      }
    }
  })

  // Build worksheet data
  const wsData = []

  // Title rows
  wsData.push([title.toUpperCase()])
  wsData.push([biz.name || ''])

  const dateRange = localStorage.getItem('debut_periode') && localStorage.getItem('fin_periode')
    ? `Du ${localStorage.getItem('debut_periode')} au ${localStorage.getItem('fin_periode')}`
    : new Date().toLocaleDateString('fr-FR')
  wsData.push([dateRange])
  wsData.push([]) // empty row

  // Header row
  const headers = ['#', 'Emplacement', 'Agent', 'Client', `Montant (${currency})`, 'Produits', 'Date', 'Heure', 'Statut']
  wsData.push(headers)

  // Data rows (already sorted by date asc)
  dataRows.forEach(function (row) {
    wsData.push([
      row.sale_number,
      row.store,
      row.agent,
      row.customer,
      Math.round(row.amount * 100) / 100, // numeric for Excel
      row.products,
      row.date,
      row.time,
      row.status,
    ])
  })

  // Totals row
  wsData.push([])
  wsData.push([
    `TOTAL (${dataRows.length} fiches)`,
    '', '', '',
    Math.round(grandTotal * 100) / 100,
    totalProducts,
    '', '', ''
  ])

  // Create workbook
  const wb = XLSX.utils.book_new()
  const ws = XLSX.utils.aoa_to_sheet(wsData)

  // Column widths
  ws['!cols'] = [
    { wch: 16 }, // #
    { wch: 20 }, // Emplacement
    { wch: 20 }, // Agent
    { wch: 22 }, // Client
    { wch: 18 }, // Montant
    { wch: 10 }, // Produits
    { wch: 12 }, // Date
    { wch: 8 },  // Heure
    { wch: 10 }, // Statut
  ]

  // AutoFilter on the header row (row index 4, 0-based)
  const headerRowIdx = 4
  ws['!autofilter'] = {
    ref: XLSX.utils.encode_range({
      s: { r: headerRowIdx, c: 0 },
      e: { r: headerRowIdx, c: headers.length - 1 }
    })
  }

  // Merge title cells
  ws['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: 4 } }, // Title
    { s: { r: 1, c: 0 }, e: { r: 1, c: 4 } }, // Business name
    { s: { r: 2, c: 0 }, e: { r: 2, c: 4 } }, // Date range
  ]

  // Style: bold title
  if (ws['A1']) ws['A1'].s = { font: { bold: true, sz: 14 } }
  if (ws['A2']) ws['A2'].s = { font: { bold: true, sz: 11 } }

  // Style: bold headers
  headers.forEach(function (_, i) {
    const cell = ws[XLSX.utils.encode_cell({ r: headerRowIdx, c: i })]
    if (cell) {
      cell.s = {
        font: { bold: true, color: { rgb: 'FFFFFF' } },
        fill: { fgColor: { rgb: options.headerColorHex || '194173' } },
        alignment: { horizontal: 'center' },
        border: {
          bottom: { style: 'thin', color: { rgb: '000000' } },
          top: { style: 'thin', color: { rgb: '000000' } },
        }
      }
    }
  })

  // Style: number format for amount column
  const amountCol = 4
  for (let r = headerRowIdx + 1; r < wsData.length; r++) {
    const cell = ws[XLSX.utils.encode_cell({ r: r, c: amountCol })]
    if (cell && typeof cell.v === 'number') {
      cell.t = 'n'
      cell.z = '#,##0.00'
    }
  }

  // Style: totals row
  const totalsRowIdx = wsData.length - 1
  for (let c = 0; c < headers.length; c++) {
    const cell = ws[XLSX.utils.encode_cell({ r: totalsRowIdx, c: c })]
    if (cell) {
      cell.s = {
        font: { bold: true },
        border: { top: { style: 'double', color: { rgb: '000000' } } },
      }
    }
  }

  XLSX.utils.book_append_sheet(wb, ws, title)
  XLSX.writeFile(wb, filename)
}
